import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { cn } from '@/lib/utils';
import { formatRelative } from '@/lib/formatters';
import { initialsOf } from '@/components/projects/PeoplePicker';
import {
  notificationHeadline,
  notificationHref,
  notificationShowsSnippet,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
  type NotificationWithContext,
} from '@/features/notifications/hooks';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const unreadQ = useUnreadNotificationCount();
  // Only fetch the list once the panel is opened, so the topbar stays
  // cheap for people who never open it — the unread count keeps polling.
  const listQ = useNotifications('all', { enabled: open });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const unread = unreadQ.data ?? 0;
  const items = (listQ.data ?? []).slice(0, 8);

  const onOpen = (n: NotificationWithContext) => {
    if (!n.read_at) markRead.mutate({ id: n.id, read: true });
    setOpen(false);
    navigate(notificationHref(n));
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          aria-label={
            unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'
          }
        >
          <Bell className="h-[18px] w-[18px]" strokeWidth={1.8} />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-fresh-salmon-dark px-1 text-[10px] font-semibold leading-none text-linen">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-medium text-foreground">
            Notifications
          </span>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {listQ.isLoading ? (
            <div className="px-3 py-8 text-center text-xs text-muted-foreground">
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="px-3 py-10 text-center text-xs text-muted-foreground">
              You're all caught up.
            </div>
          ) : (
            <ul>
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => onOpen(n)}
                    className={cn(
                      'flex w-full items-start gap-2.5 border-b border-border px-3 py-2.5 text-left last:border-b-0 hover:bg-secondary/50',
                      !n.read_at && 'bg-atmosphere/10',
                    )}
                  >
                    <Avatar className="mt-0.5 h-7 w-7 shrink-0">
                      {n.actor?.avatar_url && (
                        <AvatarImage src={n.actor.avatar_url} alt="" />
                      )}
                      <AvatarFallback className="text-[10px]">
                        {n.actor ? initialsOf(n.actor) : '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground">
                        <span className="font-medium">
                          {n.actor?.full_name ?? n.actor?.email ?? 'Someone'}
                        </span>{' '}
                        <span className="text-muted-foreground">
                          {notificationHeadline(n)}
                        </span>
                      </p>
                      {n.ticket && (
                        <p className="mt-0.5 truncate text-xs text-foreground/80">
                          {n.ticket.title}
                        </p>
                      )}
                      {notificationShowsSnippet(n) && (
                        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                          {n.snippet}
                        </p>
                      )}
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {formatRelative(n.created_at)}
                      </p>
                    </div>
                    {!n.read_at && (
                      <span
                        className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-fresh-salmon-dark"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-border p-1">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-xs"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
          >
            View all notifications
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
